import React from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import Spacer from '@component/common/spacer';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  title: string;
  description?: string;
  input?: React.ReactNode;
  complete?: React.ReactNode;
  phase?: 'input' | 'complete';
  handleComplete?: () => void;
  errorMessage?: string;
  confirmText: string;
  isPending: boolean;
  confirmDisabled?: boolean;
  confirmTheme?: ETheme;
  cancelTheme?: ETheme;
  width?: string;
  handleCancel: () => void;
  handleConfirm: () => void;
  handleSubmit?: () => void;
}

const Wrapper = styled.div<{ $width: string; $isComplete: boolean }>`
  display: flex;
  width: ${({ $width }) => $width};
  flex-direction: column;
  min-height: ${({ $isComplete }) => ($isComplete ? `calc(150px - ${size.MARGIN_2} * 2)` : 'auto')};
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  gap: ${size.MARGIN_1};
`;

const CreateModalTemplate: React.FC<IProps> = ({
  title,
  description,
  input,
  complete,
  phase = 'input',
  handleComplete,
  errorMessage,
  confirmText,
  isPending,
  confirmDisabled = false,
  confirmTheme = ETheme.CYAN,
  cancelTheme = ETheme.CYAN,
  width = size.GRIDGIO_LARGE_MODAL_WIDTH,
  handleCancel,
  handleConfirm,
  handleSubmit,
}) => (
  <Wrapper
    as={handleSubmit ? 'form' : 'div'}
    $width={width}
    $isComplete={phase === 'complete'}
    onSubmit={
      handleSubmit
        ? (event: React.FormEvent) => {
            event.preventDefault();
            if (phase === 'input') handleSubmit();
          }
        : undefined
    }>
    {phase === 'input' && (
      <>
        <TextBlock text={title} design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
        {description && (
          <>
            <Gap height={size.MARGIN_1} />
            <TextBlock text={description} design={{ text: { size: 'medium', color: color.GRAY_1 } }} />
          </>
        )}
        {input && (
          <>
            <Gap height={size.MARGIN_2} />
            {input}
          </>
        )}
        {errorMessage && (
          <>
            <Gap height={size.MARGIN_1} />
            <TextBlock text={errorMessage} design={{ text: { size: 'small', color: color.RED_2 } }} />
          </>
        )}
        <Gap height={size.MARGIN_2} />
        <ButtonContainer>
          <TextButton
            text='취소'
            theme={cancelTheme}
            kind='outlined'
            size='medium'
            shape='pill'
            width='100%'
            disabled={isPending}
            handleClick={handleCancel}
          />
          <TextButton
            text={confirmText}
            theme={confirmTheme}
            size='medium'
            shape='pill'
            width='100%'
            disabled={confirmDisabled || isPending}
            isLoading={isPending}
            handleClick={handleConfirm}
          />
        </ButtonContainer>
      </>
    )}
    {phase === 'complete' && (
      <>
        {complete}
        <Spacer />
        <TextButton
          text='확인'
          theme={confirmTheme}
          size='medium'
          shape='pill'
          width='100%'
          disabled={isPending}
          handleClick={handleComplete ?? handleCancel}
        />
      </>
    )}
  </Wrapper>
);

export default CreateModalTemplate;
